import { useEffect, useState } from 'react';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import InputField from 'flow/components/ui-components/InputField';
import useAppContext from 'flow/hooks/useAppContext';
import { routes } from 'flow/types/routes';
import { usePrevious } from 'flow/hooks';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import { validEmailRegex } from 'flow/utils';
import { addEmailAddress } from 'flow/actions/user-credential-action';

import {
    getErrorTranslation,
    getVerifyingEmailCredential,
} from 'flow/utils/credential-utils';
import FlowAlert from 'flow/components/custom-components/FlowAlert';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

const ChangeEmail = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const emailCredentials = useAppSelector((state) =>
        state.userCredentials
            .getIn([extensionKey, 'email'])
            ?.sort((a, b) =>
                a.get('credential') < b.get('credential') ? -1 : 1,
            ),
    );

    const validatingEmail = getVerifyingEmailCredential(emailCredentials);

    const [emailInput, setEmailInput] = useState(
        validatingEmail ? validatingEmail : '',
    );
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [errorTranslation, setErrorTranslation] = useState('');
    const [errorCredential, setErrorCredential] = useState('');

    const successCredential = useAppSelector((state) =>
        state.userCredentials.get('successUserCredential'),
    );

    const updateFailureReason = useAppSelector((state) =>
        state.userCredentials.get('updateFailureReason'),
    );

    useEffect(() => {
        setIsValidEmail(validEmailRegex(emailInput));
        const errorTranslation = getErrorTranslation(
            emailInput,
            emailCredentials,
            successCredential,
            updateFailureReason,
            errorCredential,
        );
        setErrorTranslation(errorTranslation);
    }, [emailInput, successCredential, updateFailureReason, errorCredential]);

    useEffect(() => {
        if (previousSuccessCredential !== successCredential) {
            setErrorCredential(emailInput);
        }
    }, [emailInput, successCredential]);

    const previousSuccessCredential = usePrevious(successCredential);

    return (
        <Page
            header={i18next.t('settings.user.credential.email_address')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.account.authentication'], {
                        extensionKey,
                    }),
                )
            }
        >
            <InputField
                placeholder={i18next.t(
                    'settings.user.credential.email_address',
                )}
                autoFocus={true}
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
            />

            {errorTranslation && (
                <p className="text-red mt-2 text-xs">
                    {i18next.t(errorTranslation)}
                </p>
            )}
            <Divider />

            <Button
                color="ghost"
                disabled={!isValidEmail || !!errorTranslation}
                onClick={() => {
                    dispatch(addEmailAddress(extensionKey, emailInput));
                }}
            >
                {i18next.t('settings.user.credential.verify_email')}
            </Button>

            <p className="mt-6 text-center text-xs text-gray-500">
                {i18next.t('settings.user.credential.verified_email_info')}
            </p>

            {successCredential && validatingEmail === emailInput && (
                <div className="mt-3">
                    <FlowAlert
                        header={i18next.t(
                            'settings.user.credential.titel_email_sent',
                        )}
                        text={i18next.t(
                            'settings.user.credential.text_email_sent',
                        )}
                    />
                </div>
            )}
        </Page>
    );
};

export default ChangeEmail;
